import type { Provider, TransactionRequest } from '@ethersproject/providers';
import type { BigNumberish, Overrides, Signer } from 'ethers';
import { Contract, ContractFactory, utils } from 'ethers';

import type {
  StarsMasterChef,
  StarsMasterChefInterface,
} from '../StarsMasterChef';

const _abi = [
  {
    inputs: [
      {
        internalType: 'address',
        name: 'starsAddress',
        type: 'address',
      },
      {
        internalType: 'address',
        name: '_admin',
        type: 'address',
      },
      {
        internalType: 'uint256[3]',
        name: '_rewardAmounts',
        type: 'uint256[3]',
      },
      {
        internalType: 'uint256[3]',
        name: '_epochs',
        type: 'uint256[3]',
      },
    ],
    stateMutability: 'nonpayable',
    type: 'constructor',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'address',
        name: 'user',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'uint256',
        name: 'pid',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'amount',
        type: 'uint256',
      },
    ],
    name: 'Deposit',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'address',
        name: 'user',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'uint256',
        name: 'pid',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'amount',
        type: 'uint256',
      },
    ],
    name: 'EmergencyWithdraw',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'address',
        name: 'user',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'uint256',
        name: 'pid',
        type: 'uint256',
      },
    ],
    name: 'RewardsCollected',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'previousAdminRole',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'newAdminRole',
        type: 'bytes32',
      },
    ],
    name: 'RoleAdminChanged',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'sender',
        type: 'address',
      },
    ],
    name: 'RoleGranted',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'sender',
        type: 'address',
      },
    ],
    name: 'RoleRevoked',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'address',
        name: 'user',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'uint256',
        name: 'pid',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'amount',
        type: 'uint256',
      },
    ],
    name: 'Withdraw',
    type: 'event',
  },
  {
    inputs: [],
    name: 'DEFAULT_ADMIN_ROLE',
    outputs: [
      {
        internalType: 'bytes32',
        name: '',
        type: 'bytes32',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'ROLE_ADMIN',
    outputs: [
      {
        internalType: 'bytes32',
        name: '',
        type: 'bytes32',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'blocks',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'poolSupply',
        type: 'uint256',
      },
    ],
    name: 'accStarsPerShareAtCurrRate',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '_allocPoint',
        type: 'uint256',
      },
      {
        internalType: 'contract IERC20',
        name: '_lpToken',
        type: 'address',
      },
      {
        internalType: 'bool',
        name: '_withUpdate',
        type: 'bool',
      },
    ],
    name: 'add',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '_pid',
        type: 'uint256',
      },
    ],
    name: 'collectRewards',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '_pid',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: '_amount',
        type: 'uint256',
      },
    ],
    name: 'deposit',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '_pid',
        type: 'uint256',
      },
    ],
    name: 'emergencyWithdraw',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    name: 'epochs',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
    ],
    name: 'getRoleAdmin',
    outputs: [
      {
        internalType: 'bytes32',
        name: '',
        type: 'bytes32',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'uint256',
        name: 'index',
        type: 'uint256',
      },
    ],
    name: 'getRoleMember',
    outputs: [
      {
        internalType: 'address',
        name: '',
        type: 'address',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
    ],
    name: 'getRoleMemberCount',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'grantRole',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'hasRole',
    outputs: [
      {
        internalType: 'bool',
        name: '',
        type: 'bool',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '_startBlock',
        type: 'uint256',
      },
    ],
    name: 'init',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'initialized',
    outputs: [
      {
        internalType: 'bool',
        name: '',
        type: 'bool',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'massUpdatePools',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '_pid',
        type: 'uint256',
      },
      {
        internalType: 'address',
        name: '_user',
        type: 'address',
      },
    ],
    name: 'pendingStars',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    name: 'poolInfo',
    outputs: [
      {
        internalType: 'contract IERC20',
        name: 'lpToken',
        type: 'address',
      },
      {
        internalType: 'uint256',
        name: 'allocPoint',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'lastRewardBlock',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'accStarsPerShare',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'poolSupply',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'poolLength',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'renounceRole',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'revokeRole',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    name: 'rewardAmounts',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '_pid',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: '_allocPoint',
        type: 'uint256',
      },
      {
        internalType: 'bool',
        name: '_withUpdate',
        type: 'bool',
      },
    ],
    name: 'set',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'stars',
    outputs: [
      {
        internalType: 'contract IERC20',
        name: '',
        type: 'address',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'starsPerBlock',
    outputs: [
      {
        internalType: 'uint256',
        name: 'amount',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'startBlock',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'totalAllocPoint',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    name: 'totalStarsPerEpoch',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '_pid',
        type: 'uint256',
      },
    ],
    name: 'updatePool',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
      {
        internalType: 'address',
        name: '',
        type: 'address',
      },
    ],
    name: 'userInfo',
    outputs: [
      {
        internalType: 'uint256',
        name: 'amount',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'rewardDebt',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: '_pid',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: '_amount',
        type: 'uint256',
      },
    ],
    name: 'withdraw',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
];

const _bytecode =
  '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';

export class StarsMasterChefFactory extends ContractFactory {
  constructor(signer?: Signer) {
    super(_abi, _bytecode, signer);
  }

  deploy(
    starsAddress: string,
    _admin: string,
    _rewardAmounts: [BigNumberish, BigNumberish, BigNumberish],
    _epochs: [BigNumberish, BigNumberish, BigNumberish],
    overrides?: Overrides & { from?: string | Promise<string> },
  ): Promise<StarsMasterChef> {
    return super.deploy(
      starsAddress,
      _admin,
      _rewardAmounts,
      _epochs,
      overrides || {},
    ) as Promise<StarsMasterChef>;
  }
  getDeployTransaction(
    starsAddress: string,
    _admin: string,
    _rewardAmounts: [BigNumberish, BigNumberish, BigNumberish],
    _epochs: [BigNumberish, BigNumberish, BigNumberish],
    overrides?: Overrides & { from?: string | Promise<string> },
  ): TransactionRequest {
    return super.getDeployTransaction(
      starsAddress,
      _admin,
      _rewardAmounts,
      _epochs,
      overrides || {},
    );
  }
  attach(address: string): StarsMasterChef {
    return super.attach(address) as StarsMasterChef;
  }
  connect(signer: Signer): StarsMasterChefFactory {
    return super.connect(signer) as StarsMasterChefFactory;
  }
  static readonly bytecode = _bytecode;
  static readonly abi = _abi;
  static createInterface(): StarsMasterChefInterface {
    return new utils.Interface(_abi) as StarsMasterChefInterface;
  }
  static connect(
    address: string,
    signerOrProvider: Signer | Provider,
  ): StarsMasterChef {
    return new Contract(address, _abi, signerOrProvider) as StarsMasterChef;
  }
}
